import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import styles from './FormattingGuideDrawer.module.scss';

const MARKDOWN_ROWS = [
  {
    syntax: '**bold**',
    render: <strong>bold</strong>,
  },
  {
    syntax: '*italic*',
    render: <em>italic</em>,
  },
  {
    syntax: '# Heading',
    render: <span className={styles.headingSample}>Heading</span>,
  },
  {
    syntax: '[title](url)',
    render: (
      <a
        className={styles.linkSample}
        href="#formatting-guide"
        onClick={(e) => e.preventDefault()}
      >
        title
      </a>
    ),
  },
  {
    syntax: '- item',
    render: <span className={styles.bulletSample}>• item</span>,
  },
  {
    syntax: '1. item',
    render: <span>1. item</span>,
  },
  {
    syntax: '> quote',
    render: <span className={styles.quoteSample}>quote</span>,
  },
];

const EMBED_SERVICES = [
  'YouTube',
  'TikTok',
  'Instagram',
  'Vimeo',
  'SoundCloud',
  'Behance',
];

const HELP_ARTICLE =
  'https://help.twine.net/en/article/how-to-add-embeds-to-a-project-138v75z/';

/**
 * Right-side reference drawer for project description Markdown + embeds.
 * No scrim — the form stays interactive while open.
 * Clicks inside `retainRef` (the description field) do not close the drawer.
 */
const FormattingGuideDrawer = ({ open, onClose, triggerRef, retainRef }) => {
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }
      if (
        triggerRef &&
        triggerRef.current &&
        triggerRef.current.contains(target)
      ) {
        return;
      }
      if (
        retainRef &&
        retainRef.current &&
        retainRef.current.contains(target)
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, onClose, triggerRef, retainRef]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <aside
      ref={drawerRef}
      className={styles.drawer}
      role="dialog"
      aria-modal="false"
      aria-labelledby="formatting-guide-title"
    >
      <div className={styles.header}>
        <div>
          <h2 id="formatting-guide-title" className={styles.title}>
            Formatting guide
          </h2>
          <p className={styles.subline}>Works in the description field.</p>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close formatting guide"
        >
          <X size={20} strokeWidth={2} />
        </button>
      </div>

      <div className={styles.body}>
        <p className={styles.eyebrow}>Markdown basics</p>
        <ul className={styles.rows}>
          {MARKDOWN_ROWS.map((row) => (
            <li key={row.syntax} className={styles.row}>
              <code className={styles.syntaxChip}>{row.syntax}</code>
              <span className={styles.render}>{row.render}</span>
            </li>
          ))}
        </ul>

        <hr className={styles.divider} />

        <p className={styles.eyebrow}>Embeds</p>
        <p className={styles.embedCopy}>
          Paste a link on its own line and it becomes a player.
        </p>
        <div className={styles.serviceChips}>
          {EMBED_SERVICES.map((service) => (
            <span key={service} className={styles.serviceChip}>
              {service}
            </span>
          ))}
        </div>

        <div className={styles.embedExample}>
          <code className={styles.syntaxChip}>
            https://youtube.com/watch?v=xK9tR…
          </code>
          <div className={styles.playerMock} aria-hidden="true">
            <span className={styles.playButton} />
          </div>
          <p className={styles.playerCaption}>Becomes an embedded player</p>
        </div>
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          Check how it looks with the Preview tab above the description.{' '}
          <a
            href={HELP_ARTICLE}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Advanced syntax
          </a>
        </p>
      </div>
    </aside>,
    document.body
  );
};

FormattingGuideDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
  retainRef: PropTypes.shape({ current: PropTypes.any }),
};

FormattingGuideDrawer.defaultProps = {
  triggerRef: null,
  retainRef: null,
};

export default FormattingGuideDrawer;
